// frontend/src/features/drivers/DriverSlice.js
// Responsibility: Keep a single, normalized source of truth for drivers across all tabs.
// This Redux slice maintains a normalized drivers store, provides rich selectors (by status/agency/filter),
// upsert/remove and status-patch helpers, and computes counts for dashboards.

import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
  createSelector,
} from '@reduxjs/toolkit';
import {
  uploadDriverPhoto as svcUpload,
  deleteDriverPhoto as svcDelete,
} from '../../services/driverService';

// ============================================================================
// Async thunks (side effects / API calls)
// ============================================================================
// A thunk is an async action creator. It lets your UI "dispatch work" that can
// take time (like an HTTP request). When it succeeds or fails, Redux Toolkit
// automatically dispatches lifecycle actions: pending/fulfilled/rejected.

// Returns the UPDATED driver from the server after upload complete
export const uploadDriverPhoto = createAsyncThunk(
  'drivers/uploadPhoto',
  // payloadCreator receives the arguments you dispatch with:
  // dispatch(uploadDriverPhoto({ id, file }))
  async ({ id, file }) => {
    // Call your service; it should return the updated driver object
    const updatedDriver = await svcUpload(id, file);
    return updatedDriver;
  }
);

// Returns the UPDATED driver (with photo removed) from the server
export const removeDriverPhoto = createAsyncThunk(
  'drivers/removePhoto',
  async ({ id }) => {
    const updatedDriver = await svcDelete(id);
    return updatedDriver;
  }
);

// ============================================================================
// Entity adapter (normalized store) + initial state
// ============================================================================
// The adapter gives you helpers (setAll, upsertOne, etc.) AND memoized selectors.
// We sort by name for a stable, user-friendly list.
const driversAdapter = createEntityAdapter({
  selectId: (driver) => driver.id,
  sortComparer: (a, b) => {
    const an = (a?.name ?? '').toString();
    const bn = (b?.name ?? '').toString();
    return an.localeCompare(bn);
  },
});

const initialState = driversAdapter.getInitialState();

// ============================================================================
// Slice: reducers (sync state changes) + extraReducers (react to thunks)
// ============================================================================
// - "reducers": local actions you define (pure, synchronous state updates)
// - "extraReducers": how this slice reacts to OTHER actions, like thunk results
const driversSlice = createSlice({
  name: 'drivers',
  initialState,
  reducers: {
    // Destructive replace (avoid on UPS pages; prefer upserts)
    setDrivers: driversAdapter.setAll,

    // Merge-friendly updates
    upsertDriver: driversAdapter.upsertOne,
    upsertDrivers: driversAdapter.upsertMany,

    // Remove only on hard delete
    removeDriver: driversAdapter.removeOne,
  },
  extraReducers: (builder) => {
    // When the upload/remove thunks complete successfully, the server
    // returns an updated driver object — we merge it in.
    builder
      .addCase(uploadDriverPhoto.fulfilled, (state, action) => {
        driversAdapter.upsertOne(state, action.payload);
      })
      .addCase(removeDriverPhoto.fulfilled, (state, action) => {
        driversAdapter.upsertOne(state, action.payload);
      });
  },
});

// Export actions for components to dispatch
export const { setDrivers, upsertDriver, upsertDrivers, removeDriver } =
  driversSlice.actions;

// Export reducer to add to your store
export default driversSlice.reducer;

// ============================================================================
// Base selectors from the adapter
// ============================================================================
export const {
  selectAll: selectAllDrivers, // array (sorted by adapter.sortComparer)
  selectById: selectDriverById, // driver by id
  selectEntities: selectDriverMap, // id -> driver
  selectIds: selectDriverIds, // [id, id, ...]
  selectTotal: selectDriverTotal, // number
} = driversAdapter.getSelectors((state) => state.drivers);

// ============================================================================
// Helpers (shared logic for selectors)
// ============================================================================
const getAgencyIdFromRow = (d) => {
  const raw = d?.agency_id ?? d?.agencyId ?? d?.agency?.id;
  return raw == null ? undefined : Number(raw);
};

const getAgencyNameFromRow = (d) =>
  d?.agency_name ?? d?.agency?.name ?? d?.agencyName ?? '';

// ============================================================================
// Convenience selectors
// ============================================================================

// Filter by a single status (legacy; simple)
export const selectDriversByStatus = (status) =>
  createSelector(selectAllDrivers, (drivers) =>
    drivers.filter((d) => d.status === status)
  );

// Active drivers for a specific agency
export const selectActiveDriversByAgency = createSelector(
  [selectAllDrivers, (_, agencyId) => agencyId],
  (drivers, agencyId) =>
    drivers.filter(
      (d) => d.status === 'Active' && Number(d.agency_id) === Number(agencyId)
    )
);

// Reserved drivers for a specific agency
export const selectReservedDriversByAgency = createSelector(
  [selectAllDrivers, (_, agencyId) => agencyId],
  (drivers, agencyId) =>
    drivers.filter(
      (d) => d.status === 'Reserved' && Number(d.agency_id) === Number(agencyId)
    )
);

// All drivers for a specific agency (any status)
export const selectDriversByAgency = createSelector(
  [selectAllDrivers, (_, agencyId) => agencyId],
  (drivers, agencyId) =>
    drivers.filter((d) => Number(d.agency_id) === Number(agencyId))
);

// ============================================================================
// Patch / Status helpers (thunk-ish sync helpers for local state only)
// ============================================================================

// Merge a partial update into an existing driver (or create a stub if missing)
export const mergeDriverPatch =
  ({ id, patch }) =>
  (dispatch, getState) => {
    const state = getState();
    const existing = selectDriverById(state, id);
    const merged = existing ? { ...existing, ...patch, id } : { id, ...patch };
    dispatch(upsertDriver(merged));
  };

// Flip status without removing the entity
export const applyStatusChange =
  ({ id, status }) =>
  (dispatch) =>
    dispatch(mergeDriverPatch({ id, patch: { status } }));

// ============================================================================
// Counters
// ============================================================================
const tally = (list) => {
  const counts = { Total: 0, Active: 0, Reserved: 0, Banned: 0 };
  for (const d of list) {
    counts.Total += 1;
    if (d?.status && counts[d.status] != null) counts[d.status] += 1;
  }
  return counts;
};

// Global totals across all drivers (UPS admin dashboard)
export const selectCountsGlobal = createSelector(selectAllDrivers, tally);

// Per-agency totals (Agency dashboards or UPS filtered views)
// Usage: const counts = useSelector(s => makeCountsByAgencySelector(agencyId)(s));
export const makeCountsByAgencySelector = (agencyId) =>
  createSelector(selectAllDrivers, (drivers) =>
    tally(drivers.filter((d) => Number(d.agency_id) === Number(agencyId)))
  );

// ============================================================================
// Parametric selector (filter-aware)
// ============================================================================

// Flexible list selector that respects { status?, agencyId?, search? }
export const makeSelectDrivers = () =>
  createSelector(
    [selectAllDrivers, (_, filters) => filters || {}],
    (drivers, { status, agencyId, agencyName, search }) => {
      const targetId =
        agencyId !== '' && agencyId != null && !Number.isNaN(Number(agencyId))
          ? Number(agencyId)
          : undefined;
      const targetName = (agencyName || '').trim().toLowerCase();

      const getDriverAgencyId = (d) => {
        const v = d?.agency_id ?? d?.agencyId ?? d?.agency?.id;
        const n = Number(v);
        return Number.isNaN(n) ? undefined : n;
      };
      const getDriverAgencyName = (d) =>
        (d?.agency_name ?? d?.agency?.name ?? '')
          .toString()
          .trim()
          .toLowerCase();

      const q = (search || '').trim().toLowerCase();

      return drivers.filter((d) => {
        if (status && d.status !== status) return false;

        // Agency filter: match by ID if present, else by name
        if (targetId !== undefined || targetName) {
          const did = getDriverAgencyId(d);
          const dname = getDriverAgencyName(d);
          const idMatches =
            targetId !== undefined && did !== undefined && did === targetId;
          const nameMatches = !!targetName && dname === targetName;
          if (!(idMatches || nameMatches)) return false;
        }

        if (q) {
          const hay = [
            d.name,
            d.email,
            d.phone,
            d.vehicle_registration_number,
            d.vehicleRegistrationNumber,
            d.agency_name,
            d.agency?.name,
          ]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();
          if (!hay.includes(q)) return false;
        }
        return true;
      });
    }
  );

// COUNTS for the current filter scope (omit status to count all three for the filter)
export const makeCountsByFilter = () =>
  createSelector([makeSelectDrivers()], (list) => tally(list));

// Tab-specific selector (Active/Reserved/Banned) that also respects global filters
export const makeSelectDriversForTab = (status) =>
  createSelector(
    [
      selectAllDrivers, // from adapter
      (state) => state.ui?.filters || {}, // { agencyId, q }
      (state) =>
        state.agencies?.list || state.agencies?.entities || [], // support either shape
    ],
    (drivers, { agencyId, q }, agenciesState) => {
      const aid = agencyId ? Number(agencyId) : undefined;
      const query = (q || '').trim().toLowerCase();

      // find selected agency name for fallback
      const agenciesArray = Array.isArray(agenciesState)
        ? agenciesState
        : Object.values(agenciesState || {});
      const selectedAgency =
        aid != null
          ? agenciesArray.find(
              (a) => Number(a?.id ?? a?.agency_id ?? a?.agencyId) === aid
            )
          : null;
      const selectedAgencyName =
        selectedAgency?.name ?? selectedAgency?.agency_name ?? '';

      return drivers.filter((d) => {
        if (!d || d.status !== status) return false;

        if (aid !== undefined) {
          const did = getAgencyIdFromRow(d);
          if (did !== undefined) {
            if (did !== aid) return false;
          } else if (selectedAgencyName) {
            // fallback by name when row lacks id
            const rowName = getAgencyNameFromRow(d);
            if (rowName !== selectedAgencyName) return false;
          }
        }

        if (query) {
          const hay = `${d.name || ''} ${d.email || ''} ${
            d.vehicle_registration_number || d.vrn || ''
          }`.toLowerCase();
          if (!hay.includes(query)) return false;
        }
        return true;
      });
    }
  );

// Counts for the CURRENT global filters (agencyId + q), across all statuses
export const selectCountsForFilters = createSelector(
  [
    selectAllDrivers,
    (state) => state.ui?.filters || {},
    (state) =>
      state.agencies?.list || state.agencies?.entities || [],
  ],
  (drivers, { agencyId, q }, agenciesState) => {
    const aid = agencyId ? Number(agencyId) : undefined;
    const query = (q || '').trim().toLowerCase();

    const agenciesArray = Array.isArray(agenciesState)
      ? agenciesState
      : Object.values(agenciesState || {});
    const selectedAgency =
      aid != null
        ? agenciesArray.find(
            (a) => Number(a?.id ?? a?.agency_id ?? a?.agencyId) === aid
          )
        : null;
    const selectedAgencyName =
      selectedAgency?.name ?? selectedAgency?.agency_name ?? '';

    const scoped = drivers.filter((d) => {
      if (!d) return false;

      if (aid !== undefined) {
        const did = getAgencyIdFromRow(d);
        if (did !== undefined) {
          if (did !== aid) return false;
        } else if (selectedAgencyName) {
          const rowName = getAgencyNameFromRow(d);
          if (rowName !== selectedAgencyName) return false;
        }
      }

      if (query) {
        const hay = `${d.name || ''} ${d.email || ''} ${
          d.vehicle_registration_number || d.vrn || ''
        }`.toLowerCase();
        if (!hay.includes(query)) return false;
      }
      return true;
    });

    return tally(scoped);
  }
);

// Global results grouped by status for a search
export const selectGlobalResults = createSelector(
  [selectAllDrivers, (state) => state.ui?.filters || {}],
  (drivers, { agencyId, q }) => {
    const aid = agencyId ? Number(agencyId) : undefined;
    const query = (q || '').trim().toLowerCase();
    const base = drivers.filter((d) => {
      if (aid !== undefined && Number(d.agency_id) !== aid) return false;
      if (!query) return false;
      const hay = `${d.name || ''} ${d.email || ''} ${
        d.vehicle_registration_number || ''
      }`.toLowerCase();
      return hay.includes(query);
    });
    return {
      Active: base.filter((d) => d.status === 'Active'),
      Reserved: base.filter((d) => d.status === 'Reserved'),
      Banned: base.filter((d) => d.status === 'Banned'),
    };
  }
);
